import { htmlSafe, type SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { SIGNIFICANT_RELATIVE_CHANGE_THRESHOLD } from 'lumen/utils/constants/total-revenue-constants';

const TEXT_COLOR_TO_ICON_COLOR_MAP = {
  'text-success-default': 'success',
  'text-critical-default': 'critical',
  'text-warning-default': 'warning',
} as const;

interface MarketTotalRevenueVarianceDisplaySignature {
  Args: {
    change?: number | null;
    formattedChange?: string | SafeString;
    formattedRelativeChange: string;
    referencePeriod: string;
    relativeChange: number | null;
  };
  Element: HTMLSpanElement;
}

export default class MarketTotalRevenueVarianceDisplay extends Component<MarketTotalRevenueVarianceDisplaySignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeText() {
    if (this.args.change === undefined) {
      return this.args.formattedRelativeChange;
    }

    return htmlSafe(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${this.args.formattedChange} (${this.args.formattedRelativeChange})`,
    ) as unknown as string;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get changeToCheck() {
    return this.args.relativeChange ?? this.args.change ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get colorChangeClass() {
    if (
      this.args.relativeChange !== null &&
      Math.abs(this.args.relativeChange) < SIGNIFICANT_RELATIVE_CHANGE_THRESHOLD
    ) {
      return 'text-warning-default';
    }

    if (this.changeToCheck === null || this.changeToCheck === 0) {
      return 'text-warning-default';
    }

    return this.changeToCheck > 0
      ? 'text-success-default'
      : 'text-critical-default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconColor() {
    return TEXT_COLOR_TO_ICON_COLOR_MAP[this.colorChangeClass];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get iconName() {
    if (this.changeToCheck === null || this.changeToCheck === 0) {
      return 'minus';
    }

    return this.changeToCheck > 0 ? 'arrow-up' : 'arrow-down';
  }

  <template>
    <span
      class="flex items-center {{this.colorChangeClass}}"
      data-test-id="variance-display"
      ...attributes
    >
      <PrismIcon
        @color={{this.iconColor}}
        @name={{this.iconName}}
        data-test-icon-color={{this.iconColor}}
        data-test-icon-name={{this.iconName}}
        data-test-id="variance-display-icon"
      />

      {{#if (eq this.changeToCheck 0)}}
        {{t
          "total_revenue.same_versus_reference_period"
          referencePeriod=@referencePeriod
        }}
      {{else}}
        {{t
          "total_revenue.value.versus.reference_period"
          htmlSafe=true
          referencePeriod=@referencePeriod
          value=this.changeText
        }}
      {{/if}}
    </span>
  </template>
}
